"use client";

import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { ImagePlus, Moon, Pencil, Sun, Upload } from "lucide-react";
import { api } from "@/lib/axios";
import type { User } from "@/lib/types/user";
import { useBranchesEnabled } from "@/hooks/api/useBranchesEnabled";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthStore } from "@/stores/authStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type SettingRow = { key: string; value: string | null; type?: string; config_group?: string };

type CompanyProfileRow = {
  id: number;
  name: string;
  logo_url?: string | null;
};

export default function ProfilePage() {
  const qc = useQueryClient();
  const { branchesEnabled } = useBranchesEnabled();
  const { user } = useAuth();
  const setUser = useAuthStore((s) => s.setUser);
  const [upi, setUpi] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [companyProfile, setCompanyProfile] = useState<CompanyProfileRow | null>(null);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companyErr, setCompanyErr] = useState<string | null>(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoMsg, setLogoMsg] = useState<string | null>(null);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const logoFileRef = useRef<HTMLInputElement>(null);

  const title = useMemo(() => (user ? `${user.name}` : "Profile"), [user]);
  const hasLogo = Boolean(companyProfile?.logo_url);

  const themePref = user?.ui_theme === "dark" ? "dark" : "light";
  const saveTheme = useMutation({
    mutationFn: async (next: "light" | "dark") => {
      const { data } = await api.put<{ user: User }>("/api/auth/profile", { ui_theme: next });

      return data.user;
    },
    onMutate: async (next) => {
      if (user) {
        setUser({ ...user, ui_theme: next });
      }
    },
    onSuccess: (u) => {
      setUser(u);
      void qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });

  useEffect(() => {
    setCompanyErr(null);
    setCompanyLoading(true);
    void api
      .get<{ profile: CompanyProfileRow }>("/api/company-profile")
      .then((r) => setCompanyProfile(r.data.profile))
      .catch((e) => {
        if (axios.isAxiosError(e)) {
          const data = e.response?.data as { error?: string } | undefined;
          setCompanyErr(data?.error ?? (e.response?.status === 403 ? "No access to company profile." : "Could not load company profile."));
        } else {
          setCompanyErr("Could not load company profile.");
        }
      })
      .finally(() => setCompanyLoading(false));
  }, []);

  useEffect(() => {
    setError(null);
    setOk(null);
    setLoading(true);
    void api
      .get<{ settings: SettingRow[] }>("/api/settings", { params: { group: "integrations" } })
      .then((r) => {
        const rows = r.data.settings ?? [];
        const row = rows.find((x) => x.key === "merchant_upi");
        setUpi(row?.value ? String(row.value) : "");
      })
      .catch((e) => {
        if (axios.isAxiosError(e)) {
          const data = e.response?.data as { error?: string } | undefined;
          setError(data?.error ?? "Could not load settings.");
        } else {
          setError("Could not load settings.");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  async function saveUpi() {
    setError(null);
    setOk(null);
    setSaving(true);
    try {
      await api.put("/api/settings", {
        settings: [{ key: "merchant_upi", value: upi.trim() }],
      });
      setOk("Saved.");
    } catch (e: unknown) {
      let msg = "Could not save.";
      if (axios.isAxiosError(e)) {
        const data = e.response?.data as { error?: string } | undefined;
        if (data?.error) {
          msg = data.error;
        } else if (e.response?.status === 403) {
          msg = "Permission denied (settings.edit).";
        }
      }
      setError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function onLogoFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) {
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      setLogoMsg("File too large (max 2 MB).");

      return;
    }
    setLogoMsg(null);
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("logo", f);
      const { data } = await api.post<{ profile: CompanyProfileRow }>("/api/company-profile/logo", fd);
      setCompanyProfile(data.profile);
      setLogoMsg("Logo saved. It will show on invoices and POS receipts.");
    } catch (err: unknown) {
      let msg = "Upload failed.";
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { error?: string } | undefined;
        msg = data?.error ?? msg;
      }
      setLogoMsg(msg);
    } finally {
      setLogoBusy(false);
    }
  }

  async function removeLogo() {
    if (!companyProfile) {
      return;
    }
    setLogoMsg(null);
    setLogoBusy(true);
    try {
      const { data } = await api.put<{ profile: CompanyProfileRow }>("/api/company-profile", {
        logo_url: null,
      });
      setCompanyProfile(data.profile);
      setLogoMsg("Logo removed.");
    } catch (err: unknown) {
      let msg = "Could not remove logo.";
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { error?: string } | undefined;
        msg = data?.error ?? msg;
      }
      setLogoMsg(msg);
    } finally {
      setLogoBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="text-sm text-muted-foreground">Account + POS settings</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Appearance</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Theme is saved per user (navbar toggle or here). Applies after sign-in on this device.
          </p>
          <div className="space-y-2">
            <Label htmlFor="ui_theme">Interface theme</Label>
            <Select
              value={themePref}
              disabled={!user || saveTheme.isPending}
              onValueChange={(v) => saveTheme.mutate(v === "dark" ? "dark" : "light")}
            >
              <SelectTrigger id="ui_theme" className="max-w-xs">
                <SelectValue placeholder="Theme" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="light">
                  <span className="flex items-center gap-2">
                    <Sun className="h-4 w-4" />
                    Light
                  </span>
                </SelectItem>
                <SelectItem value="dark">
                  <span className="flex items-center gap-2">
                    <Moon className="h-4 w-4" />
                    Dark
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Company logo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Shown on invoice PDF and POS thermal receipt. Click preview below to add or change.
            </p>
            {companyErr ? (
              <p className="text-sm text-destructive">{companyErr}</p>
            ) : null}
            {companyLoading ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : companyProfile ? (
              <button
                type="button"
                disabled={logoBusy}
                onClick={() => {
                  setLogoMsg(null);
                  setLogoDialogOpen(true);
                }}
                className="group flex w-full flex-col gap-2 rounded-lg border border-dashed bg-muted/30 p-4 text-left outline-none transition hover:border-primary/50 hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
              >
                <div className="flex min-h-[100px] items-center justify-center rounded-md border bg-background p-3">
                  {companyProfile.logo_url ? (
                    <img
                      src={companyProfile.logo_url}
                      alt="Company logo"
                      className="max-h-28 max-w-full object-contain"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 py-4 text-center">
                      <ImagePlus className="h-10 w-10 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">Add company logo</span>
                      <span className="text-xs text-muted-foreground">PNG, JPG or WebP · max 2 MB</span>
                    </div>
                  )}
                </div>
                <span className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground group-hover:text-foreground">
                  {companyProfile.logo_url ? (
                    <>
                      <Pencil className="h-3.5 w-3.5" />
                      Click to edit or replace
                    </>
                  ) : (
                    <>
                      <Upload className="h-3.5 w-3.5" />
                      Click to upload
                    </>
                  )}
                </span>
              </button>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">User</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name</span>
              <span className="font-medium">{user?.name ?? "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium">{user?.email ?? "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Role</span>
              <span className="font-medium">{user?.role ?? "—"}</span>
            </div>
            {branchesEnabled ? (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Branch</span>
                <span className="font-medium">{user?.branch_id ?? "—"}</span>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">UPI setup</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="upi">Merchant UPI ID</Label>
              <Input
                id="upi"
                placeholder="merchant@upi"
                value={upi}
                onChange={(e) => setUpi(e.target.value)}
                disabled={loading}
              />
              <p className="text-xs text-muted-foreground">
                Used in POS UPI QR.
              </p>
            </div>

            {error ? (
              <p className="text-sm text-destructive" role="alert">
                {error}
              </p>
            ) : null}
            {ok ? (
              <p className="text-sm text-emerald-700" role="status">
                {ok}
              </p>
            ) : null}

            <Button type="button" onClick={() => void saveUpi()} disabled={loading || saving}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={logoDialogOpen}
        onOpenChange={(open) => {
          setLogoDialogOpen(open);
          if (!open) {
            setLogoMsg(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{hasLogo ? "Edit company logo" : "Add company logo"}</DialogTitle>
            <DialogDescription>
              {hasLogo
                ? "Replace the image or remove it. Logo shows on invoice PDFs and POS thermal receipts."
                : "Pick an image (PNG, JPG, WebP, max 2 MB). Square logo works best on print."}
            </DialogDescription>
          </DialogHeader>

          <div className="flex min-h-[140px] items-center justify-center rounded-lg border bg-muted/40 p-4">
            {companyProfile?.logo_url ? (
              <img
                src={companyProfile.logo_url}
                alt="Company logo"
                className="max-h-40 max-w-full object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 py-6 text-center">
                <ImagePlus className="h-12 w-12 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No logo uploaded yet.</p>
              </div>
            )}
          </div>

          {logoMsg ? <p className="text-sm text-sky-800">{logoMsg}</p> : null}

          {companyProfile ? (
            <>
              <input
                ref={logoFileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                disabled={logoBusy}
                onChange={(ev) => void onLogoFile(ev)}
              />
              <div className="flex flex-wrap gap-2">
                <Button type="button" disabled={logoBusy} onClick={() => logoFileRef.current?.click()}>
                  <Upload className="mr-2 h-4 w-4" />
                  {logoBusy ? "Uploading…" : hasLogo ? "Replace logo" : "Choose image"}
                </Button>
                {hasLogo ? (
                  <Button type="button" variant="outline" disabled={logoBusy} onClick={() => void removeLogo()}>
                    Remove logo
                  </Button>
                ) : null}
              </div>
            </>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => setLogoDialogOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

